import React from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { LeadForm } from "@/components/forms/LeadForm";
import { SITE, buildWhatsAppLink, SERVICE_OPTIONS } from "@/data/site";

export default function Contact() {
  const [params] = useSearchParams();
  const rawService = params.get("service") || "";
  const plan = params.get("plan") || "";
  // Only accept a known service value for the preselect.
  const preselectService = SERVICE_OPTIONS.includes(rawService) ? rawService : "";

  useSEO({
    title: "Contact E Telecom | Talk to a Communication Expert",
    description: "Get in touch with E Telecom for WhatsApp automation, bulk WhatsApp, bulk SMS and smart telephony. Talk to an expert about your communication requirements.",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "E Telecom",
      telephone: SITE.phoneDisplay,
      email: SITE.email,
      address: { "@type": "PostalAddress", streetAddress: SITE.address },
      url: "https://etelecom.in",
    },
  });

  const details = [
    { icon: "Phone", label: "Call us", value: SITE.phoneDisplay, href: `tel:${SITE.phone}` },
    { icon: "Mail", label: "Email us", value: SITE.email, href: `mailto:${SITE.email}` },
    { icon: "MessageCircle", label: "WhatsApp", value: "Chat with our team", href: buildWhatsAppLink() },
    { icon: "MapPin", label: "Visit us", value: SITE.address, href: null },
  ];

  return (
    <>
      <section className="relative overflow-hidden et-hero-wash">
        <div className="absolute inset-0 et-dot-grid opacity-[0.35] pointer-events-none" />
        <div className="et-container relative py-14 lg:py-16 text-center">
          <Reveal>
            <span className="et-eyebrow"><Icons.Headphones className="h-3.5 w-3.5" /> Contact Us</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mt-5">Talk to an Expert</h1>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base leading-relaxed">Tell us about your communication requirements and our team will help you choose the right solution. We usually respond quickly.</p>
          </Reveal>
        </div>
      </section>

      <section className="et-section">
        <div className="et-container grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
          {/* Details */}
          <div className="lg:col-span-2 space-y-4">
            {details.map((d, i) => {
              const Icon = Icons[d.icon] || Icons.Circle;
              const inner = (
                <div className="et-card et-card-hover p-5 flex items-start gap-4">
                  <span className="et-icon-box shrink-0"><Icon className="h-5 w-5" /></span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{d.label}</div>
                    <div className="mt-1 text-sm font-medium text-slate-800 break-words">{d.value}</div>
                  </div>
                </div>
              );
              return (
                <Reveal key={d.label} delay={i * 0.05}>
                  {d.href ? <a href={d.href} target={d.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block">{inner}</a> : inner}
                </Reveal>
              );
            })}
            <Reveal delay={0.2}>
              <div className="et-navy-band rounded-2xl p-5 text-slate-300">
                <div className="text-sm font-semibold text-white">E Telecom</div>
                <div className="mt-1 text-xs">Website: {SITE.domain}</div>
                <div className="mt-3 text-xs leading-relaxed">Business communication and automation solutions — WhatsApp, SMS and smart telephony.</div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {plan && (
              <Reveal>
                <div className="et-card mb-4 p-4 flex items-center gap-3 border-l-4 border-l-[var(--et-primary)]">
                  <Icons.CheckCircle2 className="h-5 w-5 text-[var(--et-primary)] shrink-0" />
                  <p className="text-sm text-slate-600">
                    You're enquiring about the <span className="font-semibold text-slate-900">{plan}</span> plan{preselectService ? <> for <span className="font-semibold text-slate-900">{preselectService}</span></> : null}. We've pre-filled your enquiry — just add your details.
                  </p>
                </div>
              </Reveal>
            )}
            <Reveal>
              <LeadForm source="contact" testIdPrefix="contact" defaultService={preselectService} plan={plan} />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
